import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useMemo } from 'react';
import type { User } from '@xvpst/shared';
import { api } from '@/api/client';

export interface UserGroup {
  id: string;
  code: string;
  name: string;
}

interface MeResponse {
  user: User;
  groups: UserGroup[];
  permissions: string[];
  features: string[];
}

/**
 * Get current authenticated user with groups + permissions.
 */
export function useAuth() {
  const queryClient = useQueryClient();

  const { data, isLoading, error } = useQuery({
    queryKey: ['auth', 'me'],
    queryFn: () => api.get<MeResponse>('/auth/me'),
    staleTime: 10 * 60 * 1000, // 10 minutes
    retry: false,
  });

  const logoutMutation = useMutation({
    mutationFn: () => api.post('/auth/logout'),
    onSuccess: () => {
      queryClient.setQueryData(['auth', 'me'], null);
      window.location.href = '/login';
    },
  });

  return {
    user: data?.user ?? null,
    groups: data?.groups ?? [],
    permissions: data?.permissions ?? [],
    features: data?.features ?? [],
    isLoading,
    error,
    isAuthenticated: !!data?.user,
    logout: logoutMutation.mutate,
    isLoggingOut: logoutMutation.isPending,
  };
}

/**
 * Check whether a feature flag is enabled for the current user.
 * Returns false if the user is not authenticated or the flag is disabled.
 */
export function useFeature(featureKey: string): boolean {
  const { features } = useAuth();
  return useMemo(() => features.includes(featureKey), [features, featureKey]);
}

/**
 * Check whether the current user has ALL of the given permission codes.
 * Super admins always return true (bypass override).
 */
export function useHasPermission(...required: string[]): boolean {
  const { user, permissions } = useAuth();

  return useMemo(() => {
    if (!user) return false;
    if (user.isSuperAdmin) return true;
    const set = new Set(permissions);
    return required.every((p) => set.has(p));
  }, [user, permissions, required]);
}

/**
 * Initiate Google OAuth login
 */
export function useLogin() {
  const login = () => {
    window.location.href = `${import.meta.env.VITE_API_URL || ''}/auth/login/google`;
  };

  return { login };
}
